import React from 'react';
import { Briefcase } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card, CardBody, CardHeader } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Field } from '../ui/field';
import { Input } from '../ui/input';

interface OtherIncomeCardProps {
  hasOtherJob: boolean;
  onHasOtherJobChange: (value: boolean) => void;
  otherJobSalary: string;
  onOtherJobSalaryChange: (value: string) => void;
  isPaye: boolean;
  onIsPayeChange: (value: boolean) => void;
}

interface CheckboxRowProps {
  id: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  title: string;
  description: string;
}

const CheckboxRow: React.FC<CheckboxRowProps> = ({ id, checked, onCheckedChange, title, description }) => (
  <label
    htmlFor={id}
    className={cn(
      'flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-colors',
      checked ? 'border-brand/40 bg-brand/[0.06]' : 'border-line bg-raised/40 hover:border-line-strong'
    )}
  >
    <Checkbox id={id} checked={checked} onCheckedChange={(value) => onCheckedChange(value === true)} className="mt-0.5" />
    <span>
      <span className="block text-[13px] font-medium text-fg">{title}</span>
      <span className="mt-0.5 block text-xs text-fg-subtle">{description}</span>
    </span>
  </label>
);

export const OtherIncomeCard: React.FC<OtherIncomeCardProps> = ({
  hasOtherJob,
  onHasOtherJobChange,
  otherJobSalary,
  onOtherJobSalaryChange,
  isPaye,
  onIsPayeChange,
}) => (
  <Card>
    <CardHeader
      icon={<Briefcase />}
      title="Other income"
      description="Income from other jobs changes which tax bands your earnings fall into."
    />
    <CardBody className="flex flex-col gap-3">
      <CheckboxRow
        id="hasOtherJob"
        checked={hasOtherJob}
        onCheckedChange={onHasOtherJobChange}
        title="I have more than one job"
        description="Include a salary from another employer in the calculation."
      />

      {hasOtherJob && (
        <div className="grid animate-fade-in items-end gap-3 sm:grid-cols-2">
          <Field label="Annual salary from other job" htmlFor="otherJobSalary">
            <Input
              type="number"
              id="otherJobSalary"
              inputMode="decimal"
              value={otherJobSalary}
              onChange={(e) => onOtherJobSalaryChange(e.target.value)}
              step="0.01"
              min="0"
              placeholder="0.00"
              leading="£"
              className="numeric"
            />
          </Field>
          <CheckboxRow
            id="isPaye"
            checked={isPaye}
            onCheckedChange={onIsPayeChange}
            title="Other job is PAYE"
            description="Tax is already deducted by that employer. Untick if it's more self-employed work."
          />
        </div>
      )}
    </CardBody>
  </Card>
);
